// src/components/Dashboard.js
import Header from './ui/Header'
import JourneyMap from './JourneyMap'
import TrainDoorTransition from './TrainDoorTransition'
import QuickCaptureModal from './QuickCaptureModal'
import CaptureIcon from './CaptureIcon'
import CompletionJourney from './CompletionJourney'
import TransitMaps from './TransitMap'
import shareImage from '../utils/shareImage'
import { saveMoment, fetchMoments, deleteMoment, updateMomentText, addMomentToGratitudes, undoAddToGratitudes, migrateExistingMoments } from '../momentsService'
import FullScreenCamera from './FullScreenCamera'
import React, { useEffect, useState, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useAvatar } from '../hooks/useAvatar'
import { supabase } from '../supabaseClient'
import { CITIES, DAYS } from '../data/content'

/* ── Date helpers ── */
function getLocalDateStr(date = new Date()) {
  return `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`
}
function getTodayStr() { return getLocalDateStr() }

function calcCityStreak(entries, cityKey) {
  let s = 0
  for (let i = 1; i <= 28; i++) {
    const row = entries.find(e => e.city === cityKey && e.day_number === i)
    if (row?.gratitudes?.some(g => g?.trim())) s = i
    else break
  }
  return s
}

/* ── City metadata ── */
function stringToColor(str = '') {
  let hash = 0
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash)
  return `hsl(${Math.abs(hash) % 360}, 55%, 48%)`
}

const CITY_META = {
  nyc:    { label: 'New York',  system: 'MTA Subway',         color: '#EE352E' },
  paris:  { label: 'Paris',     system: 'Métro de Paris',     color: '#5F2D8A' },
  london: { label: 'London',    system: 'London Underground', color: '#E32017' },
  milan:  { label: 'Milan',     system: 'Metro di Milano',    color: '#CC0000' },
  berlin: { label: 'Berlin',    system: 'BVG U-Bahn',         color: '#F0CA00' },
  madrid: { label: 'Madrid',    system: 'Metro de Madrid',    color: '#E4032E' },
}

/* ── Metro icons ── */
function MetroIcon({ city, size = 36 }) {
  const mP = 'M212.25,205.15V81.5c0-7.09-3.9-14.53-15.6-14.53-8.85,0-12.4,3.9-16.29,11.69l-38.27,79.73h-.35L103.11,78.66C99.21,70.87,95.67,67,86.81,67,75.12,67,71.22,74.41,71.22,81.5V205.15c0,6.74,5.32,10.64,11.7,10.64,5.66,0,12-3.9,12-10.64V113h.36L130.4,184.6c2.47,5,5.67,7.8,11.34,7.8s8.85-2.84,11.33-7.8L188.15,113h.36v92.12c0,6.74,6.37,10.64,12.05,10.64,6.37,0,11.69-3.9,11.69-10.64'
  const sv = size * 0.65
  const base = { width: size, height: size, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', flexShrink: 0, overflow: 'hidden' }
  const gloss = { position:'absolute', inset:0, borderRadius:'50%', background:'radial-gradient(ellipse 72% 58% at 36% 26%,rgba(255,255,255,0.45) 0%,rgba(255,255,255,0.1) 42%,rgba(255,255,255,0) 68%)', pointerEvents:'none', zIndex:10 }
  if (city === 'nyc') return <div style={{...base,background:'#111'}}><div style={gloss}/><svg width={sv} height={sv} viewBox="0 0 284 284" style={{position:'relative',zIndex:2}}><path fill="#fff" d={mP}/></svg></div>
  if (city === 'paris') return <div style={{...base,background:'#fff',boxShadow:'0 0 0 2px #0A0082'}}><div style={gloss}/><svg width={size} height={size} viewBox="0 0 284 284" style={{position:'absolute',inset:0,zIndex:2}}><circle cx="142" cy="142" r="133" fill="#fff" stroke="#0A0082" strokeWidth="18"/><path fill="#0A0082" d={mP}/></svg></div>
  if (city === 'london') return <div style={{...base,background:'#E32017'}}><div style={gloss}/><svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{position:'absolute',inset:0,zIndex:2}}><rect x="0" y={size*0.38} width={size} height={size*0.24} fill="#003087"/><text x={size/2} y={size*0.545} textAnchor="middle" fontFamily="Helvetica Neue,Arial,sans-serif" fontWeight="900" fontSize={size*0.1} fill="#fff">UNDERGROUND</text></svg></div>
  if (city === 'milan') return <div style={{...base,background:'#CC0000'}}><div style={gloss}/><svg width={sv*0.78} height={sv*0.78} viewBox="0 0 110 110" style={{position:'relative',zIndex:2}}><line x1="12" y1="50" x2="12" y2="8" stroke="#fff" strokeWidth="16" strokeLinecap="round"/><line x1="98" y1="50" x2="98" y2="8" stroke="#fff" strokeWidth="16" strokeLinecap="round"/><line x1="12" y1="8" x2="55" y2="44" stroke="#fff" strokeWidth="16" strokeLinecap="round"/><line x1="98" y1="8" x2="55" y2="44" stroke="#fff" strokeWidth="16" strokeLinecap="round"/><rect x="0" y="49" width="110" height="12" fill="#CC0000"/><line x1="12" y1="61" x2="12" y2="102" stroke="#fff" strokeWidth="16" strokeLinecap="round"/><line x1="98" y1="61" x2="98" y2="102" stroke="#fff" strokeWidth="16" strokeLinecap="round"/><line x1="12" y1="102" x2="55" y2="66" stroke="#fff" strokeWidth="16" strokeLinecap="round"/><line x1="98" y1="102" x2="55" y2="66" stroke="#fff" strokeWidth="16" strokeLinecap="round"/></svg></div>
  return <div style={{...base,background:'var(--border)'}}><span style={{fontSize:size*0.4,fontWeight:900,color:'var(--text4)'}}>?</span></div>
}

/* ── Badges ── */
const BADGES = [
  { id: 'first_step',     icon: '✦',  name: 'First Step',   desc: 'Complete your first station',      check: s => s.totalStations >= 1 },
  { id: 'week_one',       icon: '🌱', name: 'Week One',     desc: 'Complete 7 stations',              check: s => s.totalStations >= 7 },
  { id: 'halfway',        icon: '🎯', name: 'Halfway',      desc: 'Complete 14 stations on one line', check: s => Object.values(s.cityStations).some(v => v >= 14) },
  { id: 'fifty_thanks',   icon: '⭐', name: '50 Thanks',    desc: 'Write 50 gratitudes',              check: s => s.totalGratitudes >= 50 },
  { id: 'hundred_thanks', icon: '💎', name: '100 Thanks',   desc: 'Write 100 gratitudes',             check: s => s.totalGratitudes >= 100 },
  { id: 'two_hundred',    icon: '🔮', name: '200 Thanks',   desc: 'Write 200 gratitudes',             check: s => s.totalGratitudes >= 200 },
  { id: 'multi_city',     icon: '🗺️', name: 'Globetrotter',desc: 'Active on 2 or more lines',        check: s => Object.values(s.cityStations).filter(v => v > 0).length >= 2 },
  { id: 'nyc_full',       icon: '🗽', name: 'New Yorker',   desc: 'Complete all 28 NYC stations',     check: s => (s.cityStations.nyc||0) >= 28 },
  { id: 'paris_full',     icon: '🗼', name: 'Parisien',     desc: 'Complete all 28 Paris stations',   check: s => (s.cityStations.paris||0) >= 28 },
  { id: 'london_full',    icon: '🎡', name: 'Londoner',     desc: 'Complete all 28 London stations',  check: s => (s.cityStations.london||0) >= 28 },
  { id: 'milan_full',     icon: '🛵', name: 'Milanese',     desc: 'Complete all 28 Milan stations',   check: s => (s.cityStations.milan||0) >= 28 },
  { id: 'completionist',  icon: '🏆', name: 'The Magic',    desc: 'Complete all 28 on any line',      check: s => Object.values(s.cityStations).some(v => v >= 28) },
  // NYC
  { id: 'nyc_7',    icon: '🌆', name: 'Empire Mindset',     desc: 'Complete 7 NYC stations',      check: s => (s.cityStations.nyc||0) >= 7  },
  { id: 'nyc_14',   icon: '🏙️', name: 'Skyline Gratitude',  desc: 'Complete 14 NYC stations',     check: s => (s.cityStations.nyc||0) >= 14 },
  { id: 'nyc_28',   icon: '🌸', name: 'Concrete Bloom',     desc: 'Complete all 28 NYC stations', check: s => (s.cityStations.nyc||0) >= 28 },
  // Paris
  { id: 'paris_7',  icon: '💛', name: 'Cœur de Lumière',    desc: 'Complete 7 Paris stations',      check: s => (s.cityStations.paris||0) >= 7  },
  { id: 'paris_14', icon: '🕊️', name: 'Pause de Poète',     desc: 'Complete 14 Paris stations',     check: s => (s.cityStations.paris||0) >= 14 },
  { id: 'paris_28', icon: '☕', name: 'Gratitude au Café',  desc: 'Complete all 28 Paris stations', check: s => (s.cityStations.paris||0) >= 28 },
  // London
  { id: 'london_7',  icon: '🌫️', name: 'Fog and Focus',    desc: 'Complete 7 London stations',      check: s => (s.cityStations.london||0) >= 7  },
  { id: 'london_14', icon: '🫖', name: 'Tea Time Thanks',   desc: 'Complete 14 London stations',     check: s => (s.cityStations.london||0) >= 14 },
  { id: 'london_28', icon: '⏳', name: 'Timeless Mind',     desc: 'Complete all 28 London stations', check: s => (s.cityStations.london||0) >= 28 },
  // Milan
  { id: 'milan_7',  icon: '🍃', name: 'Dolce Concentrazione', desc: 'Complete 7 Milan stations',      check: s => (s.cityStations.milan||0) >= 7  },
  { id: 'milan_14', icon: '🧵', name: 'Filo d\'Oro',          desc: 'Complete 14 Milan stations',     check: s => (s.cityStations.milan||0) >= 14 },
  { id: 'milan_28', icon: '✨', name: 'Chiarezza Espresso',   desc: 'Complete all 28 Milan stations', check: s => (s.cityStations.milan||0) >= 28 },
  // Berlin
  { id: 'berlin_7',  icon: '🪨', name: 'Echt & Roh',          desc: 'Complete 7 Berlin stations',      check: s => (s.cityStations.berlin||0) >= 7  },
  { id: 'berlin_14', icon: '🌿', name: 'Echos des Wachstums', desc: 'Complete 14 Berlin stations',     check: s => (s.cityStations.berlin||0) >= 14 },
  { id: 'berlin_28', icon: '🔓', name: 'Offener Geist',       desc: 'Complete all 28 Berlin stations', check: s => (s.cityStations.berlin||0) >= 28 },
  // Madrid
  { id: 'madrid_7',  icon: '❤️', name: 'Corazón Abierto',    desc: 'Complete 7 Madrid stations',      check: s => (s.cityStations.madrid||0) >= 7  },
  { id: 'madrid_14', icon: '🌞', name: 'Espíritu de Siesta',  desc: 'Complete 14 Madrid stations',     check: s => (s.cityStations.madrid||0) >= 14 },
  { id: 'madrid_28', icon: '💃', name: 'Llama Flamenca',      desc: 'Complete all 28 Madrid stations', check: s => (s.cityStations.madrid||0) >= 28 },
]

/* ── City row (Lines tab) ── */
function CityRow({ cityKey, meta, stations, streak, onNavigate }) {
  const pct = Math.min(100, (stations / 28) * 100)
  const displayColor = cityKey === 'berlin' ? '#b89800' : meta.color
  return (
    <div className="db-city-row" style={{cursor: onNavigate ? "pointer" : "default"}} onClick={() => onNavigate && onNavigate(cityKey)}>
      <MetroIcon city={cityKey} size={34} />
      <div className="db-city-info">
        <div className="db-city-name">{meta.label}</div>
        <div className="db-city-system">{meta.system}</div>
        <div className="db-city-bar-wrap">
          <div className="db-city-bar">
            <div className="db-city-bar-fill" style={{ width: pct + '%', background: displayColor }} />
          </div>
          <span className="db-city-count">{stations}/28</span>
        </div>
      </div>
      {streak > 0 && (
        <div className="db-city-streak" style={{ color: displayColor }}>
          <span className="db-city-streak-num">{streak}</span>
          <span className="db-city-streak-lbl">streak</span>
        </div>
      )}
    </div>
  )
}

/* ── Weekly strip ── */
function WeekStrip({ datesFlat }) {
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(); d.setDate(d.getDate() - (6 - i))
    const dateStr = getLocalDateStr(d)
    const done = datesFlat.includes(dateStr)
    const isToday = i === 6
    const isPast = !isToday && d < new Date()
    return { label: ['S','M','T','W','T','F','S'][d.getDay()], done, isToday, isPast }
  })
  return (
    <div className="db-week-strip">
      {days.map((d, i) => (
        <div key={i} className="db-week-day">
          <div className={`db-week-dot ${d.done ? 'done' : d.isToday ? 'today' : d.isPast ? 'missed' : 'future'}`} />
          <span className="db-week-lbl">{d.label}</span>
        </div>
      ))}
    </div>
  )
}

/* ── Points calculation ── */
function calcPoints(entries) {
  let pts = 0
  entries.forEach(e => {
    const gratCount = (e.gratitudes || []).filter(g => g?.trim()).length
    pts += gratCount * 10          // 10pts per gratitude
    if (gratCount >= 10) pts += 50 // 50 bonus for full station
    if (e.images?.[0]) pts += 25   // 25pts for a photo
  })
  return pts
}

/* ══════════════
   MAIN COMPONENT
══════════════ */
const PHOTO_URLS = {
  nyc:    ['nyc1', 'nyc2', 'nyc3', 'nyc4', 'nyc5', 'nyc6', 'nyc7'],
  paris:  ['paris1', 'paris2', 'paris3', 'paris4', 'paris5', 'paris6', 'paris7'],
  london: ['london1', 'london2', 'london3', 'london4', 'london5', 'london6', 'london7'],
  milan:  ['milan1', 'milan2', 'milan3', 'milan4', 'milan5', 'milan6', 'milan7'],
  berlin: ['berlin1', 'berlin2', 'berlin3', 'berlin4', 'berlin5', 'berlin6', 'berlin7'],
  madrid: ['madrid1', 'madrid2', 'madrid3', 'madrid4', 'madrid5', 'madrid6', 'madrid7'],
}
const getPhotoForStation = (city, stationNum) => {
  const pool = PHOTO_URLS[city] || PHOTO_URLS.nyc
  const name = pool[(stationNum - 1) % pool.length]
  return 'https://magic.soappower.com/images/cities/' + name + '.jpg'
}
// Keep for share modal where no station context exists
const getRandomPhoto = (city) => {
  const pool = PHOTO_URLS[city] || PHOTO_URLS.nyc
  const name = pool[Math.floor(Math.random() * pool.length)]
  return 'https://magic.soappower.com/images/cities/' + name + '.jpg'
}
function SharePostcardModal({ sc, shareCopied, setShareCopied, onClose }) {
  const cardRef = React.useRef(null)
  const [generatingImage, setGeneratingImage] = React.useState(false)
  const shareText = '"' + sc.text + '"\n\nStation ' + sc.stationNum + ' · ' + sc.cityData.label + ' Line — The Magic Journey'
  const photo = sc.overridePhoto || getPhotoForStation(sc.city, sc.stationNum)
  const isMoment = !!sc.overridePhoto
  // Device detection
  const isIOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent)
  const isIOSChrome = isIOS && /CriOS/.test(navigator.userAgent)
  const isIOSSafari = isIOS && !isIOSChrome && /Safari/.test(navigator.userAgent)
  const canShareFiles = typeof navigator !== 'undefined' && !!navigator.share && !!navigator.canShare
  const hasNativeShare = typeof navigator !== 'undefined' && !!navigator.share

  const handleCopy = () => {
    navigator.clipboard?.writeText(shareText).then(() => {
      setShareCopied(true)
      setTimeout(() => setShareCopied(false), 2500)
    })
  }

  const captureCard = async () => {
    // 1st try: html2canvas screenshot of rendered card
    try {
      const el = cardRef.current
      if (!el) throw new Error('no ref')
      const h2c = await new Promise((resolve) => {
        if (window.html2canvas) { resolve(window.html2canvas); return }
        const s = document.createElement('script')
        s.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js'
        s.onload = () => resolve(window.html2canvas)
        s.onerror = () => resolve(null)
        document.head.appendChild(s)
      })
      if (!h2c) throw new Error('html2canvas failed to load')
      const rect = el.getBoundingClientRect()
      const canvas = await h2c(el, {
        scale: 3,
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
        width: rect.width,
        height: rect.height,
        logging: false,
      })
      if (canvas) return canvas
    } catch(e) { console.warn('html2canvas failed, using fallback:', e.message) }

    // 2nd try: solid color card with text — always works, no dependencies
    try {
      const W = 1080, H = 720
      const canvas = document.createElement('canvas')
      canvas.width = W; canvas.height = H
      const ctx = canvas.getContext('2d')
      const color = sc.cityData.primaryColor || '#c00'
      ctx.fillStyle = color; ctx.fillRect(0, 0, W, H)
      const grad = ctx.createLinearGradient(0, 0, 0, H)
      grad.addColorStop(0, 'rgba(0,0,0,0.3)'); grad.addColorStop(1, 'rgba(0,0,0,0.75)')
      ctx.fillStyle = grad; ctx.fillRect(0, 0, W, H)
      ctx.fillStyle = '#fff'; ctx.textAlign = 'left'
      ctx.font = 'bold 28px Arial, sans-serif'
      ctx.fillText('✦  THE MAGIC JOURNEY', 64, 72)
      ctx.font = '20px Arial, sans-serif'; ctx.fillStyle = 'rgba(255,255,255,0.6)'
      ctx.fillText((sc.cityData.label || '').toUpperCase() + '  ·  STATION ' + sc.stationNum, 64, 112)
      ctx.fillStyle = color; ctx.fillRect(64, 128, 240, 3)
      ctx.fillStyle = '#fff'; ctx.font = 'italic bold 52px Arial, sans-serif'; ctx.textAlign = 'center'
      const words = ('"' + sc.text + '"').split(' '); const lines = []; let line = ''
      for (const word of words) {
        const test = line ? line + ' ' + word : word
        if (ctx.measureText(test).width > W - 128 && line) { lines.push(line); line = word } else line = test
      }
      if (line) lines.push(line)
      const startY = H * 0.42
      lines.forEach((l, i) => { ctx.shadowColor = 'rgba(0,0,0,0.8)'; ctx.shadowBlur = 12; ctx.fillText(l, W/2, startY + i * 68) })
      ctx.shadowBlur = 0; ctx.font = '20px Arial, sans-serif'; ctx.fillStyle = 'rgba(255,255,255,0.4)'
      ctx.fillText('magic.soappower.com', W/2, H - 40)
      return canvas
    } catch(e) { console.error('fallback canvas failed:', e); return null }
  }
  const handleShare = async () => {
    setGeneratingImage(true)
    let blob = null
    try {
      const canvas = await captureCard()
      if (canvas) blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.93))
    } catch(e) { console.error('capture error', e) }
    setGeneratingImage(false)

    if (blob) {
      await shareImage(blob, 'magic-postcard.jpg', shareText)
      return
    }

    // No image — share text only
    if (navigator.share) {
      try { await navigator.share({ title: 'The Magic Journey', text: shareText }) }
      catch (e) { if (e.name !== 'AbortError') handleCopy() }
    } else { handleCopy() }
  }

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }} />
      <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', pointerEvents: 'none' }}>
        <div className="share-modal-wrap" style={{ pointerEvents: 'all', maxWidth: '380px', width: '100%' }}>
          <div ref={cardRef} className={`share-card${isMoment ? ' share-card--moment' : ''}`} style={{ '--share-city': sc.cityData.primaryColor, background: sc.cityData.primaryColor }}>
            <img src={photo} alt="" className="share-card-photo" onError={e => { e.target.style.display = 'none' }} />
            <div className="share-card-vintage-tint" />
            <div className="share-card-overlay" />
            <div className="share-card-inner">
              <div className="share-card-eyebrow">
                <span className="share-card-city-name">{sc.cityData.label}</span>
                <span className="share-card-sep">·</span>
                <span>Station {sc.stationNum}</span>
              </div>
              <p className="share-card-quote">"{sc.text}"</p>
              <div className="share-card-footer">
                <span className="share-card-brand">The Magic Journey</span>
                {sc.location && (
                  <span className="share-card-location" style={{display:'flex',alignItems:'center',gap:3}}>
                    <svg width="8" height="11" viewBox="0 0 14 18" fill="none">
                      <path d="M7 0C3.13 0 0 3.13 0 7c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5C5.62 9.5 4.5 8.38 4.5 7S5.62 4.5 7 4.5 9.5 5.62 9.5 7 8.38 9.5 7 9.5z" fill="rgba(255,255,255,0.7)"/>
                    </svg>
                    {sc.location}
                  </span>
                )}
                <span className="share-card-mark">✦</span>
              </div>
            </div>
          </div>
          <p className="share-hint">{shareCopied === 'open' ? 'Long-press the image → Share → WhatsApp, Instagram…' : isIOSChrome ? 'Opens image — long press to share to WhatsApp, Instagram…' : hasNativeShare ? 'Share to WhatsApp, Instagram, or anywhere' : 'Copy text to paste anywhere'}</p>
          <div className="share-actions">
            <button className="share-btn-primary" style={{ background: sc.cityData.primaryColor }} onClick={handleShare}>
              {generatingImage ? '⏳ Preparing…' : shareCopied === 'open' ? '↗ Opened — long press to share' : shareCopied ? '✓ Copied!' : (isIOSSafari || (!isIOS && canShareFiles)) ? '↑ Share as Image' : isIOSChrome ? '↑ Share as Image' : hasNativeShare ? '↑ Share as Image' : 'Copy to share'}
            </button>
            {hasNativeShare && (
              <button className="share-btn-secondary" onClick={handleCopy}>
                {shareCopied ? '✓ Copied!' : 'Copy text instead'}
              </button>
            )}
            <button className="share-btn-close" onClick={onClose}>✕ Close</button>
          </div>
        </div>
      </div>
    </>
  )
}


/* ── Past Station Drawer Component ── */
function PastStationDrawerComponent({ pastStation, city, cityColor, session, uploadingPhoto, photoSaved, setUploadingPhoto, setPhotoSaved, setStats, setPastStation, onClose }) {
  const { day, entry } = pastStation
  const stationData = DAYS[city]?.[day - 1]
  const gratitudes = entry?.gratitudes?.filter(g => g?.trim()) || []
  const dateStr = entry?.completed_at || entry?.updated_at
  const dateLabel = dateStr ? new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null
  const entryDate = dateStr ? new Date(dateStr) : null
  const canEdit = entryDate ? (Date.now() - entryDate.getTime()) < 24 * 60 * 60 * 1000 : false
  const [editingIdx, setEditingIdx] = React.useState(null)
  const [editVal, setEditVal] = React.useState('')
  const [savingEdit, setSavingEdit] = React.useState(false)

  const handleEditSave = async (idx) => {
    setSavingEdit(true)
    const newGrats = [...(entry?.gratitudes || [])]
    newGrats[idx] = editVal
    const { error } = await supabase
      .from('journal_entries')
      .update({ gratitudes: newGrats })
      .eq('user_id', session.user.id)
      .eq('city', city)
      .eq('day_number', day)
    if (!error) {
      setStats(prev => ({ ...prev, entries: prev.entries.map(e2 => e2.city === city && e2.day_number === day ? { ...e2, gratitudes: newGrats } : e2) }))
      setPastStation(prev2 => ({ ...prev2, entry: { ...prev2.entry, gratitudes: newGrats } }))
    }
    setSavingEdit(false)
    setEditingIdx(null)
  }

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadingPhoto(true)
    const reader = new FileReader()
    reader.onload = async (ev) => {
      const base64 = ev.target.result
      const { error } = await supabase
        .from('journal_entries')
        .update({ images: [base64] })
        .eq('user_id', session.user.id)
        .eq('city', city)
        .eq('day_number', day)
      if (!error) {
        setPhotoSaved(true)
        setStats(prev => ({
          ...prev,
          entries: prev.entries.map(e2 => e2.city === city && e2.day_number === day ? { ...e2, images: [base64] } : e2),
          moments: entry?.images?.[0]
            ? prev.moments.map(m => m.city === city && m.day === day ? { ...m, img: base64 } : m)
            : [...prev.moments, { city, day, img: base64, grat: gratitudes[0] || '' }]
        }))
        setPastStation(prev2 => ({ ...prev2, entry: { ...prev2.entry, images: [base64] } }))
      }
      setUploadingPhoto(false)
    }
    reader.readAsDataURL(file)
  }

  return (
    <div className="ps-overlay" onClick={onClose}>
      <div className="ps-drawer" onClick={e => e.stopPropagation()}>
        <div className="ps-drawer-handle" />
        <div className="ps-header">
          <div className="ps-header-left">
            <div className="ps-station-dot" style={{ background: cityColor }} />
            <div>
              <div className="ps-station-num" style={{ color: cityColor }}>Station {day}</div>
              <div className="ps-station-title">{stationData?.title || ''}</div>
            </div>
          </div>
          <div className="ps-header-right">
            {dateLabel && <div className="ps-date">{dateLabel}</div>}
            <button className="ps-close" onClick={onClose}>✕</button>
          </div>
        </div>
        <div className="ps-body">
          <div className="ps-photo-wrap">
            {pastStation.entry?.images?.[0] ? (
              <div className="ps-photo-existing">
                <img src={pastStation.entry.images[0]} alt="Moment" className="ps-photo-img" />
                <label className="ps-photo-change">
                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoUpload} />
                  {uploadingPhoto ? 'Saving…' : photoSaved ? '✓ Saved' : 'Change photo'}
                </label>
              </div>
            ) : (
              <label className="ps-photo-add" style={{ borderColor: cityColor }}>
                <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handlePhotoUpload} />
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                  <circle cx="12" cy="13" r="4"/>
                </svg>
                <span>{uploadingPhoto ? 'Saving…' : photoSaved ? '✓ Saved' : 'Add a photo'}</span>
              </label>
            )}
          </div>
          {gratitudes.length > 0 ? (
            <div className="ps-gratitudes">
              <div className="ps-grat-label">
                {gratitudes.length} gratitude{gratitudes.length !== 1 ? 's' : ''} from this station
                {canEdit && <span className="ps-edit-hint"> · tap to edit</span>}
              </div>
              {(entry?.gratitudes || []).map((g, i) => (
                <div key={i} className="ps-grat-item" onClick={() => { if (canEdit && editingIdx !== i) { setEditingIdx(i); setEditVal(g || '') } }}>
                  <div className="ps-grat-num" style={{ color: cityColor }}>{String(i + 1).padStart(2, '0')}</div>
                  {editingIdx === i ? (
                    <div className="ps-grat-edit-wrap">
                      <textarea className="ps-grat-edit" value={editVal} onChange={e => setEditVal(e.target.value)} autoFocus rows={3} onClick={e => e.stopPropagation()} />
                      <div className="ps-grat-edit-actions">
                        <button className="ps-grat-save" style={{ background: cityColor }} onClick={e => { e.stopPropagation(); handleEditSave(i) }} disabled={savingEdit}>{savingEdit ? 'Saving…' : 'Save'}</button>
                        <button className="ps-grat-cancel" onClick={e => { e.stopPropagation(); setEditingIdx(null) }}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div className="ps-grat-text" style={canEdit ? { cursor: 'pointer' } : {}}>
                      {g || <span style={{opacity:0.3}}>empty</span>}
                      {canEdit && <span className="ps-grat-edit-icon"> ✎</span>}
                    </div>
                  )}
                </div>
              ))}
              {!canEdit && <div className="ps-edit-locked">Editing locked after 24 hours</div>}
            </div>
          ) : (
            <div className="ps-empty">No gratitudes recorded for this station.</div>
          )}
        </div>
      </div>
    </div>
  )
}

/* ── Rolodex Tab Component ── */
function RolodexTab({ city, cityColor, cityMeta, cityStations, stats, onBack, onOpenStation, onNavigateToCity }) {
  const days = DAYS[city] || []
  const todayNum = Math.min(cityStations + 1, 28)
  const [rolodexIdx, setRolodexIdx] = React.useState(Math.min(todayNum - 1, 27))

  let startY = 0
  const handleTouchStart = e => { startY = e.touches[0].clientY }
  const handleTouchEnd = e => {
    const diff = startY - e.changedTouches[0].clientY
    if (Math.abs(diff) > 30) {
      if (diff > 0) setRolodexIdx(i => Math.min(i + 1, 27))
      else setRolodexIdx(i => Math.max(i - 1, 0))
    }
  }

  const isDone = (rolodexIdx + 1) <= cityStations

  return (
    <div className="db-journey-rolodex">
      <div className="db-rolodex-today">
        <div className="db-rolodex-today-label" style={{ color: cityColor }}>{cityMeta.label} Line</div>
        <div className="db-rolodex-today-station">Today · Station {todayNum}</div>
        <div className="db-rolodex-today-title">{days[Math.min(todayNum - 1, 27)]?.title || ''}</div>
        {cityStations < 28 && (
          <button className="db-rolodex-cta" style={{ background: cityColor }} onClick={onBack}>
            Continue Journey →
          </button>
        )}
      </div>

      <div className="db-rolodex-label">Browse Stations</div>
      <div className="db-rolodex-stack" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        {[-2, -1, 0, 1, 2].map(offset => {
          const idx = rolodexIdx + offset
          if (idx < 0 || idx > 27) return <div key={offset} className="db-rolodex-card phantom" />
          const day = days[idx]
          const stationNum = idx + 1
          const done = stationNum <= cityStations
          const curr = stationNum === todayNum
          const future = stationNum > todayNum
          const entry = stats.entries?.find(e => e.city === city && e.day_number === stationNum)
          const scale = offset === 0 ? 1 : Math.abs(offset) === 1 ? 0.88 : 0.76
          const opacity = offset === 0 ? 1 : Math.abs(offset) === 1 ? 0.55 : 0.25
          const translateY = offset * 88
          return (
            <div
              key={idx}
              className={"db-rolodex-card" + (offset === 0 ? " active" : "") + (future ? " locked" : "")}
              style={{
                transform: `translateY(${translateY}px) scale(${scale})`,
                opacity,
                zIndex: 10 - Math.abs(offset),
                borderColor: offset === 0 && done ? day?.color || cityColor : offset === 0 && curr ? cityColor : undefined,
              }}
              onClick={() => {
                if (offset !== 0) { setRolodexIdx(idx); return }
                if (done) onOpenStation(stationNum, entry || null)
              }}
            >
              <div className="db-rolodex-card-num" style={{ color: done ? day?.color || cityColor : curr ? cityColor : "var(--text4)" }}>
                {future ? "🔒" : `${stationNum}`}
              </div>
              <div className="db-rolodex-card-content">
                <div className="db-rolodex-card-title">{future ? "Locked" : day?.title}</div>
                <div className="db-rolodex-card-sub">
                  {future ? `Unlocks at Station ${stationNum - 1} completion`
                    : curr ? "Today's station"
                    : done ? (entry?.gratitudes?.filter(g => g?.trim()).length || 0) + " gratitudes written"
                    : ""}
                </div>
              </div>
              {done && entry?.images?.[0] && (
                <div className="db-rolodex-card-img"><img src={entry.images[0]} alt="" /></div>
              )}
              {done && offset === 0 && (
                <div className="db-rolodex-card-badge" style={{ background: day?.color || cityColor }}>✓</div>
              )}
            </div>
          )
        })}
      </div>

      <div className="db-rolodex-arrows">
        <button className="db-rolodex-arrow" onClick={() => setRolodexIdx(i => Math.max(i - 1, 0))} disabled={rolodexIdx === 0}>↑</button>
        <div className="db-rolodex-counter">{rolodexIdx + 1} / 28</div>
        <button className="db-rolodex-arrow" onClick={() => setRolodexIdx(i => Math.min(i + 1, 27))} disabled={rolodexIdx === 27}>↓</button>
      </div>
    </div>
  )
}

export default function Dashboard({ session, city, onBack, onNavigateToCity, theme, toggleTheme, initialTab = 'overview' }) {
  const [trainDoor, setTrainDoor] = useState(null)
  const [quickCapture, setQuickCapture] = useState(false)
  const [showDashboardCamera, setShowDashboardCamera] = useState(false)
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState(initialTab)
  const tabsRef = React.useRef(null)
  const activeTabRef = React.useRef(null)
  const [lightbox, setLightbox] = useState(null)
  const [lbEditing, setLbEditing] = useState(false)
  const [lbEditText, setLbEditText] = useState('')
  const [lbSaving, setLbSaving] = useState(false)
  const [lbDeleting, setLbDeleting] = useState(false)
  const [lbConfirmDelete, setLbConfirmDelete] = useState(false)
  const [pastStation, setPastStation] = useState(null)
  const [uploadingPhoto, setUploadingPhoto] = useState(false)
  const [photoSaved, setPhotoSaved] = useState(false)
  const [leaderboard, setLeaderboard] = useState(null)
  const [allEntries, setAllEntries] = useState(null) // for postcards tab
  const [sharePostcard, setSharePostcard] = useState(null) // postcard share modal
  const [postcardFilter, setPostcardFilter] = useState(null) // city filter pill
  const [postcardEdit, setPostcardEdit] = useState(null)
  const [passportCity, setPassportCity] = useState(null)
  const [momentsFeed, setMomentsFeed] = useState(null) // unified moments from new table
  const [addToast, setAddToast] = useState(null) // { text, undo }
  const [addToastTimer, setAddToastTimer] = useState(null)
  const [badgeToast, setBadgeToast] = useState(null) // { icon, name }
  const prevBadgeIdsRef = useRef(null)
  const [postcardEditSaving, setPostcardEditSaving] = useState(false)
  const [generatingImage, setGeneratingImage] = useState(false)
  const [shareCopied, setShareCopied] = useState(false)
  const dbShareCardRef = React.useRef(null)
  const [lbLoading, setLbLoading] = useState(false)
  const { displayName, avatarUrl, initials } = useAvatar(session)
  const cityMeta = CITY_META[city] || CITY_META.nyc
  const cityColor = city === 'berlin' ? '#b89800' : cityMeta.color

  const loadStats = useCallback(async () => {
    setLoading(true)
    const { data, error } = await supabase
      .from('journal_entries')
      .select('city, day_number, gratitudes, grat_thanks, completed_at, updated_at, images')
      .eq('user_id', session.user.id)

    if (!error && data) {
      const cityStations = { nyc: 0, paris: 0, london: 0, milan: 0, madrid: 0, berlin: 0 }
      const cityDates    = { nyc: [], paris: [], london: [], milan: [], madrid: [], berlin: [] }
      let totalGratitudes = 0, totalThanks = 0
      const moments = []
      data.forEach(row => {
        if (!row.city || cityStations[row.city] === undefined) return
        const hasGrat = row.gratitudes?.some(g => g?.trim())
        if (!hasGrat) return
        cityStations[row.city]++
        const dateStr = row.completed_at || row.updated_at
        if (dateStr) cityDates[row.city].push(dateStr)
        totalGratitudes += (row.gratitudes || []).filter(g => g?.trim()).length
        totalThanks += (row.grat_thanks || []).filter(t => t >= 3).length
        if (row.images?.[0]) moments.push({ city: row.city, day: row.day_number, img: row.images[0], grat: row.gratitudes?.[0] || '', location: null })
      })
      const cityStreaks = {}
      Object.keys(cityStations).forEach(c => { cityStreaks[c] = calcCityStreak(data, c) })
      const combinedStreak = Math.max(...Object.values(cityStreaks), 0)
      const allDates = [...new Set(Object.values(cityDates).flat())]
      const totalStations = Object.values(cityStations).reduce((a, b) => a + b, 0)
      const myPoints = calcPoints(data)
      setStats({ cityStations, cityDates, cityStreaks, allDates, combinedStreak, totalGratitudes, totalThanks, totalStations, moments, entries: data, myPoints })
    }
    setLoading(false)
  }, [session.user.id])

  const loadLeaderboard = useCallback(async () => {
    setLbLoading(true)
    try {
      const token = session.access_token
      if (!token) { setLbLoading(false); setLeaderboard([]); return }
      const resp = await fetch('https://vukbnqpdkxpnbjxpyuxz.supabase.co/functions/v1/leaderboard', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InZ1a2JucXBka3hwbmJqeHB5dXh6Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzE2MjU0NTMsImV4cCI6MjA4NzIwMTQ1M30.OaNbmKj2JLbZFmIb4Pu8amKiRJT1O4TZpJveNzqjHHg',
        },
        body: JSON.stringify({})
      })
      if (resp.ok) {
        const data = await resp.json()
        const myUid = session.user.id
        const ranked = (data || []).map((u, idx) => ({
          uid: u.user_id,
          name: u.display_name || (u.user_id === myUid ? displayName : `Traveller ${idx + 1}`),
          avatar: u.avatar_url || (u.user_id === myUid ? avatarUrl : null),
          points: u.points,
          stations: u.stations,
        }))
        setLeaderboard(ranked)
      } else {
        setLeaderboard([])
      }
    } catch (e) {
      setLeaderboard([])
    }
    setLbLoading(false)
  }, [session.user.id, displayName, avatarUrl])

  const loadAllEntries = useCallback(async () => {
    if (allEntries) return
    const { data, error } = await supabase
      .from('journal_entries')
      .select('city, day_number, gratitudes, images')
      .eq('user_id', session.user.id)
    if (!error && data) {
      const map = {}
      data.forEach(row => {
        if (!map[row.city]) map[row.city] = {}
        map[row.city][row.day_number] = row
      })
      setAllEntries(map)
    }
  }, [allEntries, session.user.id])

  useEffect(() => { if (activeTab === 'postcards' || activeTab === 'journey' || quickCapture || passportCity) loadAllEntries() }, [activeTab, quickCapture, passportCity, loadAllEntries])

  useEffect(() => { loadStats() }, [loadStats])
  useEffect(() => { if (activeTab === 'leaderboard' && !leaderboard) loadLeaderboard() }, [activeTab]) // eslint-disable-line

  // Load unified moments feed from new table
  const loadMomentsFeed = useCallback(async () => {
    // Run migration first (only runs once per user)
    await migrateExistingMoments(session.user.id)
    const { data } = await fetchMoments(session.user.id)
    setMomentsFeed(data || [])
  }, [session.user.id])

  useEffect(() => {
    if (activeTab === 'moments') loadMomentsFeed()
  }, [activeTab, loadMomentsFeed])
  useEffect(() => { if (activeTab === 'postcards' || activeTab === 'journey' || quickCapture || passportCity) loadAllEntries() }, [activeTab, quickCapture, passportCity, loadAllEntries])

  useEffect(() => {
    if (activeTabRef.current && tabsRef.current) {
      const tab = activeTabRef.current
      const container = tabsRef.current
      const tabLeft = tab.offsetLeft
      const tabWidth = tab.offsetWidth
      const containerWidth = container.offsetWidth
      const scrollTo = tabLeft - (containerWidth / 2) + (tabWidth / 2)
      container.scrollTo({ left: scrollTo, behavior: 'smooth' })
    }
  }, [activeTab])

  const streak = stats?.combinedStreak || 0
  const cityStations = stats?.cityStations?.[city] || 0
  const allDatesFlat = stats?.allDates?.map(d => {
    const dt = new Date(d.includes('T') ? d : d + 'T12:00:00')
    return getLocalDateStr(dt)
  }) || []
  const unlockedBadges = stats ? BADGES.filter(b => b.check(stats)) : []

  /* ── Badge toast: fire when a new badge is earned ── */
  useEffect(() => {
    if (!stats) return
    const currentIds = new Set(unlockedBadges.map(b => b.id))
    if (prevBadgeIdsRef.current === null) {
      // First load — seed the ref silently, no toast
      prevBadgeIdsRef.current = currentIds
      return
    }
    const newlyEarned = unlockedBadges.filter(b => !prevBadgeIdsRef.current.has(b.id))
    prevBadgeIdsRef.current = currentIds
    if (newlyEarned.length === 0) return
    // Show the first newly earned badge; queue any extras with a small delay
    newlyEarned.forEach((badge, i) => {
      setTimeout(() => {
        setBadgeToast({ icon: badge.icon, name: badge.name })
        setTimeout(() => setBadgeToast(null), 3500)
      }, i * 4000)
    })
  }, [stats]) // eslint-disable-line

  /* ── Past Station Drawer ── */
  const PastStationDrawer = pastStation ? (
    <PastStationDrawerComponent
      pastStation={pastStation}
      city={city}
      cityColor={cityColor}
      session={session}
      uploadingPhoto={uploadingPhoto}
      photoSaved={photoSaved}
      setUploadingPhoto={setUploadingPhoto}
      setPhotoSaved={setPhotoSaved}
      setStats={setStats}
      setPastStation={setPastStation}
      onClose={() => setPastStation(null)}
    />
  ) : null

  /* ── Lightbox ── */
  if (lightbox) {
    const lbColor = CITY_META[lightbox.city]?.color || '#fff'
    const lbMeta = CITY_META[lightbox.city] || {}

    const handleLbEditSave = async () => {
      if (!lbEditText.trim()) return
      setLbSaving(true)
      // Try moment_caption first, fall back to updating gratitudes[0]
      let result = await supabase
        .from('journal_entries')
        .update({ moment_caption: lbEditText.trim() })
        .eq('user_id', session.user.id)
        .eq('city', lightbox.city)
        .eq('day_number', lightbox.day)
      if (result.error) {
        // Fallback: update gratitudes array
        const entry = allEntries?.[lightbox.city]?.[lightbox.day]
        const updatedGrats = [...(entry?.gratitudes || Array(10).fill(''))]
        updatedGrats[0] = lbEditText.trim()
        result = await supabase
          .from('journal_entries')
          .update({ gratitudes: updatedGrats })
          .eq('user_id', session.user.id)
          .eq('city', lightbox.city)
          .eq('day_number', lightbox.day)
      }
      if (!result.error) {
        const updated = { ...lightbox, grat: lbEditText.trim() }
        setLightbox(updated)
        setStats(prev => ({
          ...prev,
          moments: prev.moments.map(m =>
            m.city === lightbox.city && m.day === lightbox.day ? { ...m, grat: lbEditText.trim() } : m
          )
        }))
        setLbEditing(false)
      }
      setLbSaving(false)
    }

    const handleLbDelete = async () => {
      setLbDeleting(true)
      let result = await supabase
        .from('journal_entries')
        .update({ images: null, moment_caption: null })
        .eq('user_id', session.user.id)
        .eq('city', lightbox.city)
        .eq('day_number', lightbox.day)
      // Fallback if moment_caption column doesn't exist
      if (result.error) {
        result = await supabase
          .from('journal_entries')
          .update({ images: null })
          .eq('user_id', session.user.id)
          .eq('city', lightbox.city)
          .eq('day_number', lightbox.day)
      }
      if (!result.error) {
        setStats(prev => ({
          ...prev,
          moments: prev.moments.filter(m => !(m.city === lightbox.city && m.day === lightbox.day))
        }))
        setLightbox(null)
        setLbConfirmDelete(false)
      }
      setLbDeleting(false)
    }

    return (
      <div className="db-lightbox" onClick={() => { setLightbox(null); setLbEditing(false); setLbConfirmDelete(false) }}>
        <div className="db-lightbox-inner" onClick={e => e.stopPropagation()}>
          <img src={lightbox.img} alt="Moment" className="db-lightbox-img" />
          <div className="db-lightbox-info">
            <div className="db-lightbox-eyebrow">
              <div className="db-lightbox-dot" style={{ background: lbColor }} />
              <span style={{ color: lbColor }}>{lbMeta.label}</span>
              <span className="db-lightbox-sep">·</span>
              <span>Station {lightbox.day}</span>
            {lightbox.location && (
              <div className="lb-location-row">
                <svg width="12" height="16" viewBox="0 0 14 18" fill="none" style={{flexShrink:0}}>
                <path d="M7 0C3.13 0 0 3.13 0 7c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5C5.62 9.5 4.5 8.38 4.5 7S5.62 4.5 7 4.5 9.5 5.62 9.5 7 8.38 9.5 7 9.5z" fill={lbColor}/>
              </svg>
                <span className="lb-location-text">{lightbox.location}</span>
              </div>
            )}
            </div>

            {lbEditing ? (
              <div className="lb-edit-wrap">
                <textarea
                  className="lb-edit-input"
                  value={lbEditText}
                  onChange={e => setLbEditText(e.target.value)}
                  rows={3}
                  autoFocus
                  onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleLbEditSave() }}
                />
                <div className="lb-edit-btns">
                  <button className="lb-edit-save" style={{ background: lbColor }} onClick={handleLbEditSave} disabled={lbSaving}>
                    {lbSaving ? 'Saving…' : 'Save'}
                  </button>
                  <button className="lb-edit-cancel" onClick={() => setLbEditing(false)}>Cancel</button>
                </div>
              </div>
            ) : (
              <p
                className={`db-lightbox-grat lb-grat-tappable ${!lightbox.grat ? 'lb-grat-empty' : ''}`}
                onDoubleClick={() => { setLbEditText(lightbox.grat || ''); setLbEditing(true) }}
                onClick={e => { if (e.detail === 2) { setLbEditText(lightbox.grat || ''); setLbEditing(true) } }}
                title="Double-tap to edit"
              >
                {lightbox.grat ? `"${lightbox.grat}"` : 'Double-tap to add a caption…'}
              </p>
            )}
          </div>

          <div className="db-lightbox-actions">
            <button className="db-lightbox-share" onClick={() => {
              setLightbox(null)
              setSharePostcard({
                text: lightbox.grat || '✦',
                stationNum: lightbox.day,
                city: lightbox.city,
                cityData: lbMeta,
                overridePhoto: lightbox.img,
              })
            }}>↑ Share as Postcard</button>

            {lbConfirmDelete ? (
              <div className="lb-delete-confirm">
                <span>Delete this moment?</span>
                <button className="lb-delete-yes" onClick={handleLbDelete} disabled={lbDeleting}>
                  {lbDeleting ? '…' : 'Yes, delete'}
                </button>
                <button className="lb-delete-no" onClick={() => setLbConfirmDelete(false)}>Cancel</button>
              </div>
            ) : (
              <button className="lb-delete-btn" onClick={() => setLbConfirmDelete(true)}>Delete moment</button>
            )}
          </div>
          <button className="db-lightbox-close" onClick={() => { setLightbox(null); setLbEditing(false); setLbConfirmDelete(false) }}>✕</button>
        </div>
      </div>
    )
  }

  if (loading || !stats) return (
    <div className="db-page">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div style={{ color: 'var(--text4)', fontSize: '0.7rem', letterSpacing: '2px', textTransform: 'uppercase' }}>Loading…</div>
      </div>
    </div>
  )

  return (
    <div className="db-page dashboard-page">

      <Header
        back={{ label: <MetroIcon city={city} size={20} />, onClick: onBack }}
        action={toggleTheme
          ? <button className="db-theme-btn" onClick={toggleTheme}>{theme === 'dark' ? '☀️' : '🌑'}</button>
          : null
        }
      />

      {/* Profile card */}
      <div className="db-profile-card" style={{ '--city': cityColor }}>
        <div className="db-profile-card-stripe" style={{ background: cityColor }} />
        <div className="db-profile-card-body">
          <div className="db-avatar-ring-wrap">
            {/* Multicolor arc ring — one segment per completed station */}
            <svg className="db-avatar-arc" viewBox="0 0 100 100" width="72" height="72">
              {(() => {
                const cx = 50, cy = 50, r = 44
                const circumference = 2 * Math.PI * r
                const total = 28
                const segmentAngle = 360 / total
                const gap = 2 // degrees gap between segments
                const days = DAYS[city] || []
                return Array.from({ length: total }, (_, i) => {
                  const done = i < cityStations
                  const current = i === cityStations
                  const color = done ? (days[i]?.color || cityColor) : current ? '#22c55e' : 'var(--border)'
                  const startAngle = -90 + i * segmentAngle + gap / 2
                  const endAngle = startAngle + segmentAngle - gap
                  const toRad = deg => (deg * Math.PI) / 180
                  const x1 = cx + r * Math.cos(toRad(startAngle))
                  const y1 = cy + r * Math.sin(toRad(startAngle))
                  const x2 = cx + r * Math.cos(toRad(endAngle))
                  const y2 = cy + r * Math.sin(toRad(endAngle))
                  const largeArc = segmentAngle - gap > 180 ? 1 : 0
                  return (
                    <path
                      key={i}
                      d={`M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`}
                      fill="none"
                      stroke={color}
                      strokeWidth={done || current ? 4.5 : 3}
                      strokeLinecap="round"
                      opacity={done || current ? 1 : 0.25}
                    />
                  )
                })
              })()}
            </svg>
            <div className="db-avatar">
              {avatarUrl ? <img src={avatarUrl} alt="avatar" /> : <span>{(initials || displayName || 'U')[0].toUpperCase()}</span>}
            </div>
          </div>
          <div className="db-profile-info">
            <div className="db-profile-name">{displayName}</div>
            <div className="db-profile-city" style={{ color: cityColor }}>
              <MetroIcon city={city} size={14} />
              <span>{cityMeta.label} Line</span>
            </div>
          </div>
          <div className="db-profile-stats-mini">
            <div className="db-profile-stat-mini">
              <span className="db-profile-stat-val" style={{ color: cityColor }}>{stats.myPoints}</span>
              <span className="db-profile-stat-lbl">points</span>
            </div>
            <div className="db-profile-stat-divider" />
            <div className="db-profile-stat-mini">
              <span className="db-profile-stat-val" style={{ color: cityColor }}>{unlockedBadges.length}</span>
              <span className="db-profile-stat-lbl">badges</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="db-tabs-wrap">
      <div className="db-tabs" ref={tabsRef}>
        {[
          { id: 'overview',     label: 'Overview' },
          { id: 'journey',      label: 'Journey' },
          { id: 'maps',         label: 'Maps' },
          { id: 'moments',      label: 'Moments' },
          { id: 'postcards',    label: 'Postcards' },
          { id: 'leaderboard',  label: 'Leaderboard' },
          { id: 'lines',        label: 'Lines' },
          { id: 'badges',       label: 'Badges' },
          { id: 'about',        label: 'About' },
        ].map(({ id, label }) => (
          <button
            key={id}
            ref={activeTab === id ? activeTabRef : null}
            className={'db-tab' + (activeTab === id ? ' active' : '')}
            style={activeTab === id ? { background: cityColor, color: '#fff', borderColor: cityColor } : {}}
            onClick={() => setActiveTab(id)}
          >
            {label}
            {id === 'moments' && stats.moments?.length > 0 && (
              <span className="db-tab-badge" style={{ background: '#fff', color: cityColor }}>
                {stats.moments.length}
              </span>
            )}
            {id === 'postcards' && stats.totalStations > 0 && (
              <span className="db-tab-badge" style={{ background: '#fff', color: cityColor }}>
                {stats.totalStations}
              </span>
            )}
          </button>
        ))}
      </div>
      </div>

      <div className="db-content">

        {/* ── OVERVIEW ── */}
        {activeTab === 'overview' && (
          <>
            <div className="db-journey-hero">
              <div className="db-journey-hero-label">
                <span style={{ color: cityColor }}>✦</span>{' '}{cityMeta.label} Line
              </div>
              <div className="db-journey-hero-sub">
                {cityStations === 0 ? 'Your journey begins at Station 1'
                  : cityStations >= 28 ? 'Journey complete ✦'
                  : `Station ${cityStations} of 28 — ${28 - cityStations} ahead`}
              </div>
              <div className="db-metro-map">
                <div className="db-metro-track">
                  <div className="db-metro-track-bg" />
                  <div className="db-metro-track-fill" style={{
                    width: `${(cityStations / 28) * 100}%`,
                    background: cityStations > 0
                      ? `linear-gradient(to right, ${DAYS[city]?.slice(0, cityStations).map(d => d.color).join(', ') || cityColor})`
                      : cityColor
                  }} />
                </div>
                <div className="db-metro-stops">
                  {Array.from({ length: 28 }, (_, i) => {
                    const done = i < cityStations
                    const current = i === cityStations
                    const isMile = [6,13,20,27].includes(i)
                    const stopColor = DAYS[city]?.[i]?.color || cityColor
                    const nextStopColor = DAYS[city]?.[i + 1]?.color || cityColor
                    return (
                      <div key={i} className={'db-metro-stop' + (isMile ? ' milestone' : '')}>
                        <div
                          className={'db-metro-stop-dot' + (done ? ' done' : current ? ' current' : '')}
                          style={done ? { background: stopColor, borderColor: stopColor, cursor: 'pointer' } : current ? { borderColor: '#22c55e', background: '#fff' } : {}}
                          onClick={done ? () => {
                            const entry = stats.entries?.find(e => e.city === city && e.day_number === (i + 1))
                            setPastStation({ day: i + 1, entry: entry || null })
                            setPhotoSaved(false)
                          } : undefined}
                        />
                        {isMile && <div className="db-metro-stop-label" style={{ color: done ? stopColor : 'var(--text4)' }}>{i + 1}</div>}
                      </div>
                    )
                  })}
                </div>
              </div>
              {streak > 0 && (
                <div className="db-streak-quiet">{streak} station{streak !== 1 ? 's' : ''} in a row ✦</div>
              )}
            </div>

            <div className="db-quiet-stats">
              <div className="db-quiet-stat">
                <span className="db-quiet-stat-val" style={{ color: cityColor }}>{stats.totalGratitudes}</span>
                <span className="db-quiet-stat-lbl">gratitudes</span>
              </div>
              <div className="db-quiet-stat-div" />
              <div className="db-quiet-stat">
                <span className="db-quiet-stat-val" style={{ color: cityColor }}>{stats.totalStations}</span>
                <span className="db-quiet-stat-lbl">stations</span>
              </div>
              <div className="db-quiet-stat-div" />
              <div className="db-quiet-stat">
                <span className="db-quiet-stat-val" style={{ color: cityColor }}>{stats.myPoints}</span>
                <span className="db-quiet-stat-lbl">points</span>
              </div>
            </div>

            {(() => {
              const CITY_META_LIST = [
                { id: 'nyc',    label: 'New York', color: '#EE352E' },
                { id: 'paris',  label: 'Paris',    color: '#5B2D8E' },
                { id: 'london', label: 'London',   color: '#E32017' },
                { id: 'milan',  label: 'Milano',   color: '#CC0000' },
                { id: 'berlin', label: 'Berlin',   color: '#F0CA00' },
                { id: 'madrid', label: 'Madrid',   color: '#E4032E' },
              ]
              const completed = CITY_META_LIST.filter(m => (stats?.cityStations?.[m.id] || 0) >= 28)
              if (!completed.length) return null
              return (
                <div className="db-section">
                  <div className="db-section-label">✦ Passports Earned — {completed.length} of 6</div>
                  <div className="db-passports-row">
                    {completed.map(m => (
                      <button key={m.id} className="db-passport-stamp" onClick={() => setPassportCity(m.id)} style={{ '--stamp-color': m.color }}>
                        <div className="db-passport-stamp-inner">
                          <div className="db-passport-stamp-circle" style={{ borderColor: m.color }}>
                            <div className="db-passport-stamp-dash" style={{ borderColor: m.color }} />
                            <div className="db-passport-stamp-id" style={{ color: m.color }}>{m.id.toUpperCase()}</div>
                            <div className="db-passport-stamp-n" style={{ color: m.color }}>28</div>
                            <div className="db-passport-stamp-lbl" style={{ color: m.color }}>STATIONS</div>
                          </div>
                          <div className="db-passport-stamp-name" style={{ color: m.color }}>{m.label}</div>
                        </div>
                      </button>
                    ))}
                    {CITY_META_LIST.filter(m => (stats?.cityStations?.[m.id] || 0) < 28).map(m => (
                      <div key={m.id} className="db-passport-stamp db-passport-stamp--locked">
                        <div className="db-passport-stamp-inner">
                          <div className="db-passport-stamp-circle db-passport-stamp-circle--locked">
                            <div className="db-passport-stamp-id db-passport-stamp-id--locked">{m.id.toUpperCase()}</div>
                            <div className="db-passport-stamp-n db-passport-stamp-n--locked">{stats?.cityStations?.[m.id] || 0}</div>
                            <div className="db-passport-stamp-lbl db-passport-stamp-lbl--locked">/ 28</div>
                          </div>
                          <div className="db-passport-stamp-name db-passport-stamp-name--locked">{m.label}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })()}

            {Object.values(stats.cityStations).some(v => v > 0) && (
              <div className="db-section">
                <div className="db-section-label">All Lines</div>
                {Object.entries(CITY_META).map(([c2, m]) => {
                  const n = stats.cityStations[c2] || 0
                  if (n === 0) return null
                  return (
                    <div key={c2} className="db-line-quiet-row" style={{cursor:"pointer"}} onClick={() => setTrainDoor(c2)}>
                      <div className="db-line-quiet-dot" style={{ background: m.color }} />
                      <div className="db-line-quiet-name">{m.label}</div>
                      <div className="db-line-quiet-track">
                        <div className="db-line-quiet-fill" style={{ width: `${(n/28)*100}%`, background: m.color }} />
                      </div>
                      <div className="db-line-quiet-count" style={{ color: m.color }}>{n}<span style={{opacity:0.4}}>/28</span></div>
                    </div>
                  )
                })}
              </div>
            )}

            <div className="db-section">
              <div className="db-section-label">This Week</div>
              <WeekStrip datesFlat={allDatesFlat} />
            </div>
          </>
        )}

{/* ── JOURNEY ── */}
        {activeTab === 'journey' && (
          <div>
            {/* ── METRO MAP hero ── */}
            <JourneyMap
              city={city}
              cityProgress={cityStations}
              mode="dashboard"
              onStationPress={(num) => {
                const entry = stats.entries?.find(e => e.city === city && e.day_number === num)
                if (num <= cityStations) {
                  setPastStation({ day: num, entry: entry || null })
                  setPhotoSaved(false)
                }
              }}
            />

            <TransitMaps stats={stats} />
          </div>
        )}

        {activeTab === 'maps' && (
          <TransitMaps stats={stats} />
        )}

                {/* ── MOMENTS ── */}
        {activeTab === 'moments' && (() => {
          const feed = momentsFeed
          const todayStation = Math.min((stats?.cityStations?.[city] || 0) + 1, 28)
          // Check if today's gratitude slots are full (10 written)
          const todayEntry = allEntries?.[city]?.[todayStation]
          const todayGratCount = (todayEntry?.gratitudes || []).filter(g => g?.trim()).length
          const todayFull = todayGratCount >= 10

          const handleAddToToday = async (m) => {
            const result = await addMomentToGratitudes({
              userId: session.user.id,
              city,
              dayNumber: todayStation,
              momentText: m.text || '',
            })
            if (result.full) {
              setAddToast({ text: "Today's journey is complete ✨", undo: null })
            } else if (!result.error) {
              const prev = result.previousGratitudes
              if (addToastTimer) clearTimeout(addToastTimer)
              setAddToast({
                text: '✦ Added to today\'s list',
                undo: async () => {
                  await undoAddToGratitudes({ userId: session.user.id, city, dayNumber: todayStation, previousGratitudes: prev })
                  setAddToast(null)
                }
              })
              const t = setTimeout(() => setAddToast(null), 4000)
              setAddToastTimer(t)
            }
          }

          return (
            <div className="db-section">
              <div className="db-section-label">
                Magic Moments — {feed?.length || 0} captured
              </div>
              <button className="stops-capture-cta db-capture-cta" onClick={() => setShowDashboardCamera(true)}>
                <CaptureIcon color={cityColor} size={16} />
                <span className="stops-capture-cta-text">CAPTURE THE MOMENT</span>
              </button>

              {!feed ? (
                <div className="db-moments-empty"><p>Loading moments…</p></div>
              ) : !feed.length ? (
                <div className="db-moments-empty"><p>No moments yet. Capture something beautiful.</p></div>
              ) : (
                <div className="db-moments-list">
                  {feed.map((m) => {
                    const mColor = m.city ? (CITY_META[m.city]?.color || '#999') : '#999'
                    const isStation = !!m.city && !!m.day_number
                    const date = new Date(m.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
                    return (
                      <div key={m.id} className="db-moment-row">
                        {/* Photo */}
                        <div className="db-moment-row-img" onClick={() => setLightbox({ img: m.image_url, grat: m.text, city: m.city, day: m.day_number, location: m.location_name, momentId: m.id })}>
                          <img src={m.image_url} alt="" />
                          {isStation && (
                            <div className="db-moment-row-badge" style={{ background: mColor }}>
                              {CITY_META[m.city]?.label?.[0]}{m.day_number}
                            </div>
                          )}
                        </div>

                        {/* Info */}
                        <div className="db-moment-row-body">
                          <div className="db-moment-row-meta">
                            {isStation
                              ? <span style={{ color: mColor, fontSize: 10, fontWeight: 700, letterSpacing: 1 }}>{CITY_META[m.city]?.label} · Station {m.day_number}</span>
                              : <span style={{ color: 'var(--text4)', fontSize: 10, letterSpacing: 1 }}>Free capture</span>
                            }
                            <span style={{ color: 'var(--text4)', fontSize: 10 }}>{date}</span>
                          </div>
                          {m.text && (
                            <div className="db-moment-row-text">"{m.text.length > 70 ? m.text.slice(0, 70) + '…' : m.text}"</div>
                          )}
                          {m.location_name && (
                            <div className="db-moment-row-loc">
                              <svg width="8" height="11" viewBox="0 0 14 18" fill="none" style={{flexShrink:0}}>
                                <path d="M7 0C3.13 0 0 3.13 0 7c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5C5.62 9.5 4.5 8.38 4.5 7S5.62 4.5 7 4.5 9.5 5.62 9.5 7 8.38 9.5 7 9.5z" fill={mColor}/>
                              </svg>
                              {m.location_name}
                            </div>
                          )}
                          {/* Add to today CTA */}
                          <button
                            className="db-moment-add-btn"
                            onClick={() => handleAddToToday(m)}
                            disabled={todayFull}
                          >
                            {todayFull ? "Today complete ✨" : "+ Add to today's list ✨"}
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Undo toast */}
              {addToast && (
                <div className="moments-toast">
                  <span>{addToast.text}</span>
                  {addToast.undo && (
                    <button className="moments-toast-undo" onClick={addToast.undo}>Undo</button>
                  )}
                </div>
              )}
            </div>
          )
        })()}

        {/* ── POSTCARDS ── */}
        {activeTab === 'postcards' && (() => {
          // Build list of all completed stations across all cities with a gratitude
          const allCards = []
          Object.entries(allEntries || {}).forEach(([cityKey, cityEntries]) => {
            const meta = CITY_META[cityKey]
            if (!meta) return
            // Only show cities user has started journaling on
            const hasStarted = Object.values(cityEntries).some(e => (e?.gratitudes || []).some(g => g?.trim()?.length > 2))
            if (!hasStarted) return
            Object.entries(cityEntries).forEach(([dayNum, entry]) => {
              const grats = (entry?.gratitudes || []).filter(g => g?.trim()?.length > 2)
              if (!grats.length) return
              allCards.push({
                city: cityKey,
                day: Number(dayNum),
                meta,
                entry,
                grat: grats[0],
                allGrats: grats,
              })
            })
          })
          allCards.sort((a, b) => b.day - a.day)

          // Cities the user has actually started — for filter pills
          const activeCities = [...new Set(allCards.map(c => c.city))]

          return (
            <div className="db-section">
              <div className="db-section-label">Your Postcards — {allCards.length} from the journey</div>
              {!allCards.length ? (
                <div className="db-moments-empty">
                  <p>Complete stations to collect postcards from each city.</p>
                </div>
              ) : (
                <>
                  {activeCities.length > 1 && (
                    <div className="db-postcard-pills">
                      <button
                        className={'db-postcard-pill' + (!postcardFilter ? ' active' : '')}
                        onClick={() => setPostcardFilter(null)}
                      >ALL</button>
                      {activeCities.map(city => {
                        const meta = CITY_META[city]
                        return (
                          <button
                            key={city}
                            className={'db-postcard-pill' + (postcardFilter === city ? ' active' : '')}
                            style={postcardFilter === city ? { background: meta.color, borderColor: meta.color } : {}}
                            onClick={() => setPostcardFilter(postcardFilter === city ? null : city)}
                          >{meta.label.toUpperCase()}</button>
                        )
                      })}
                    </div>
                  )}
                  <div className="db-postcards-grid">
                    {allCards.filter(card => !postcardFilter || card.city === postcardFilter).map((card, i) => {
                      const photo = getPhotoForStation(card.city, card.day)
                      const cityColor = card.city === 'berlin' ? '#b89800' : card.meta.color
                      return (
                        <div key={i} className="db-postcard-card-wrap">
                        <div
                          className="db-postcard-card"
                          style={{ background: cityColor }}
                          onClick={() => setSharePostcard({
                            text: card.grat,
                            stationNum: card.day,
                            cityData: { ...card.meta, primaryColor: cityColor },
                            city: card.city,
                            photo: photo,
                          })}
                        >
                          <img
                            src={photo}
                            alt={card.meta.label}
                            className="db-postcard-photo"
                            onError={e => { e.target.style.display = 'none' }}
                          />
                          <div className="db-postcard-tint" />
                          <div className="db-postcard-vignette" />
                          <div className="db-postcard-inner">
                            <div className="db-postcard-stamp">
                              <div className="db-postcard-postmark">
                                <span className="db-postcard-postmark-city">{card.meta.label}</span>
                                <span className="db-postcard-postmark-mark">✦</span>
                                <span className="db-postcard-postmark-num">{card.day}</span>
                              </div>
                            </div>
                            <div className="db-postcard-quote-block">
                              <p className="db-postcard-quote">"{card.grat.length > 80 ? card.grat.slice(0, 80) + '…' : card.grat}"</p>
                              <div className="db-postcard-meta">
                                <span>{card.meta.label}</span>
                                <span className="db-postcard-dot">·</span>
                                <span>Station {card.day}</span>
                              </div>
                            </div>
                          </div>
                          <div className="db-postcard-share-hint">↑ Share</div>
                        </div>
                        <button className="db-postcard-edit-link" onClick={() => setPostcardEdit({ card, text: card.grat })}>
                          Edit
                        </button>
                        </div>
                      )
                    })}
                  </div>
                </>
              )}
            </div>
          )
        })()}

        {/* ── LEADERBOARD ── */}
        {activeTab === 'leaderboard' && (() => {
          const GHOSTS = [
            { uid: 'ghost-1', name: 'Sofia K.', city: 'Paris', stations: 31, points: 4200, ghost: true, color: '#7B68EE' },
            { uid: 'ghost-2', name: 'Marcus T.', city: 'NYC', stations: 24, points: 3100, ghost: true, color: '#E8734A' },
            { uid: 'ghost-3', name: 'Yuki N.', city: 'London', stations: 18, points: 2250, ghost: true, color: '#4A9E8E' },
          ]
          const realCount = leaderboard?.length || 0
          // Fill up to 5 slots with ghosts if fewer than 5 real users
          const ghostsNeeded = Math.max(0, 5 - realCount)
          const ghosts = GHOSTS.slice(0, ghostsNeeded)
          // Merge real + ghosts, re-sort by points
          const allRows = [...(leaderboard || []), ...ghosts].sort((a, b) => b.points - a.points)
          const inviteUrl = 'https://magic.soappower.com'
          const inviteMsg = encodeURIComponent(`✦ Join me on The Magic Journal — a daily gratitude journey through the world's great cities. ${inviteUrl}`)
          const waUrl = `https://wa.me/?text=${inviteMsg}`
          return (
            <div className="db-section">
              <div className="db-section-label">Journey Board</div>
              <div className="db-lb-explainer">Activity points only — gratitudes &amp; photos. No private content shown.</div>
              {lbLoading ? (
                <div className="db-lb-loading">Loading travellers…</div>
              ) : (
                <div className="db-lb-list">
                  {allRows.map((user, i) => {
                    const isMe = user.uid === session.user.id
                    const medal = i === 0 ? '✦' : i === 1 ? '✧' : i === 2 ? '○' : null
                    const avatarBg = user.ghost ? user.color : (!user.avatar ? stringToColor(user.name) : undefined)
                    return (
                      <div key={user.uid} className={'db-lb-row' + (isMe ? ' me' : '') + (user.ghost ? ' ghost' : '')} style={isMe ? { borderColor: cityColor } : {}}>
                        <div className="db-lb-rank" style={isMe ? { color: cityColor } : {}}>
                          {medal ? <span className="db-lb-medal">{medal}</span> : <span>{i + 1}</span>}
                        </div>
                        <div className="db-lb-avatar" style={avatarBg ? { background: avatarBg } : {}}>
                          {user.avatar
                            ? <img src={user.avatar} alt={user.name} />
                            : <span>{user.name[0].toUpperCase()}</span>
                          }
                        </div>
                        <div className="db-lb-info">
                          <div className="db-lb-name">{isMe ? 'You' : user.name}</div>
                          <div className="db-lb-sub">{user.ghost ? `${user.city} · ${user.stations} stations` : `${user.stations} station${user.stations !== 1 ? 's' : ''}`}</div>
                        </div>
                        <div className="db-lb-points" style={isMe ? { color: cityColor } : {}}>
                          {user.points.toLocaleString()}
                          <span className="db-lb-pts-lbl">pts</span>
                        </div>
                      </div>
                    )
                  })}
                  {/* Invite slot */}
                  <a href={waUrl} target="_blank" rel="noopener noreferrer" className="db-lb-invite">
                    <div className="db-lb-invite-icon">＋</div>
                    <div className="db-lb-invite-info">
                      <div className="db-lb-invite-label">Empty seat — invite a friend</div>
                      <div className="db-lb-invite-sub">Share via WhatsApp</div>
                    </div>
                    <div className="db-lb-invite-arrow">↗</div>
                  </a>
                </div>
              )}
              <div className="db-lb-scoring">
                <div className="db-lb-scoring-title">How points work</div>
                <div className="db-lb-scoring-row"><span>10 pts</span><span>per gratitude written</span></div>
                <div className="db-lb-scoring-row"><span>50 pts</span><span>bonus for full station (10 gratitudes)</span></div>
                <div className="db-lb-scoring-row"><span>25 pts</span><span>for adding a photo moment</span></div>
              </div>
            </div>
          )
        })()}

        {/* ── LINES ── */}
        {activeTab === 'lines' && (
          <div className="db-section">
            <div className="db-section-label">All Lines</div>
            {Object.entries(CITY_META).map(([c, m]) => (
              <CityRow key={c} cityKey={c} meta={m} stations={stats.cityStations[c] || 0} streak={stats.cityStreaks[c] || 0} onNavigate={(ck) => setTrainDoor(ck)} />
            ))}
          </div>
        )}

        {/* ── BADGES ── */}
        {activeTab === 'badges' && (
          <div className="db-section">
            <div className="db-section-label">Badges — {unlockedBadges.length}/{BADGES.length} unlocked</div>
            <div className="db-badges-grid">
              {BADGES.map(badge => {
                const unlocked = badge.check(stats)
                return (
                  <div key={badge.id} className={'db-badge' + (unlocked ? ' unlocked' : '')}>
                    <div className="db-badge-icon" style={unlocked ? { background: cityColor, color: '#fff' } : {}}>{badge.icon}</div>
                    <div className="db-badge-name">{badge.name}</div>
                    <div className="db-badge-desc">{badge.desc}</div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* ── ABOUT ── */}
        {activeTab === 'about' && (
          <div className="db-section">
            <div className="db-about-wrap">
              <div className="db-about-mark">✦</div>
              <h2 className="db-about-title">The Magic Journal</h2>
              <p className="db-about-sub">A 28-day gratitude journey inspired by Rhonda Byrne's The Magic.</p>
              <a href="https://magic.soappower.com" target="_blank" rel="noopener noreferrer" className="db-about-link" style={{ background: cityColor }}>
                Learn more →
              </a>
              <button className="db-about-signout" onClick={() => supabase.auth.signOut()}>
                Sign out
              </button>
            </div>
          </div>
        )}

      </div>
      {PastStationDrawer}

      {/* ── Postcard share modal ── */}
      {sharePostcard && createPortal(
        <SharePostcardModal
          sc={sharePostcard}
          shareCopied={shareCopied}
          setShareCopied={setShareCopied}
          onClose={() => { setSharePostcard(null); setShareCopied(false) }}
        />,
        document.body
      )}

      {quickCapture && (
        <QuickCaptureModal
          session={session}
          currentCity={city}
          defaultDay={stats?.cityStations?.[city] || null}
          allEntries={allEntries}
          hideStationPicker={true}
          onClose={() => setQuickCapture(false)}
          onSaved={(moment) => {
            setStats(prev => ({
              ...prev,
              moments: prev.moments.some(m => m.city === moment.city && m.day === moment.day)
                ? prev.moments.map(m => m.city === moment.city && m.day === moment.day ? { ...m, img: moment.img, grat: moment.grat } : m)
                : [...prev.moments, moment]
            }))
            setQuickCapture(false)
          }}
        />
      )}

      {/* Full screen camera from moments tab */}
      {showDashboardCamera && (
        <FullScreenCamera
          cityColor={cityColor}
          onClose={(reason) => {
            setShowDashboardCamera(false)
            if (reason === 'picker') setQuickCapture(true)
          }}
          onCapture={async (dataUrl) => {
            setShowDashboardCamera(false)
            await saveMoment({
              userId: session.user.id,
              imageUrl: dataUrl,
              city,
              dayNumber: null,
            })
            const { data } = await fetchMoments(session.user.id)
            setMomentsFeed(data || [])
          }}
        />
      )}

      {/* Passport viewer */}
      {passportCity && (
        <CompletionJourney
          session={session}
          city={passportCity}
          entries={allEntries?.[passportCity] || {}}
          onClose={() => setPassportCity(null)}
          onSharePassport={() => {}}
        />
      )}

      {postcardEdit && (
        <div className="edit-sheet-backdrop" onClick={() => setPostcardEdit(null)}>
          <div className="edit-sheet" onClick={e => e.stopPropagation()}>
            {/* Drag handle */}
            <div className="edit-sheet-handle" />
            <div className="edit-sheet-eyebrow">
              <span style={{ color: postcardEdit.card.city === 'berlin' ? '#b89800' : CITY_META[postcardEdit.card.city]?.color }}>
                {CITY_META[postcardEdit.card.city]?.label}
              </span>
              <span style={{ opacity: 0.4 }}> · Station {postcardEdit.card.day}</span>
            </div>
            <textarea
              className="edit-sheet-textarea"
              value={postcardEdit.text}
              onChange={e => setPostcardEdit(prev => ({ ...prev, text: e.target.value }))}
              rows={3}
              autoFocus
            />
            <div className="edit-sheet-btns">
              <button
                className="edit-sheet-save"
                style={{ background: postcardEdit.card.city === 'berlin' ? '#b89800' : CITY_META[postcardEdit.card.city]?.color }}
                disabled={postcardEditSaving}
                onClick={async () => {
                  if (!postcardEdit.text.trim()) return
                  setPostcardEditSaving(true)
                  const updatedGrats = [...(postcardEdit.card.entry?.gratitudes || [])]
                  if (updatedGrats.length > 0) updatedGrats[0] = postcardEdit.text.trim()
                  const { error } = await supabase
                    .from('journal_entries')
                    .update({ gratitudes: updatedGrats })
                    .eq('user_id', session.user.id)
                    .eq('city', postcardEdit.card.city)
                    .eq('day_number', postcardEdit.card.day)
                  if (!error) {
                    setAllEntries(prev => ({
                      ...prev,
                      [postcardEdit.card.city]: {
                        ...(prev?.[postcardEdit.card.city] || {}),
                        [postcardEdit.card.day]: {
                          ...(prev?.[postcardEdit.card.city]?.[postcardEdit.card.day] || {}),
                          gratitudes: updatedGrats
                        }
                      }
                    }))
                    setPostcardEdit(null)
                  }
                  setPostcardEditSaving(false)
                }}
              >{postcardEditSaving ? 'Saving…' : 'Save'}</button>
              <button className="edit-sheet-cancel" onClick={() => setPostcardEdit(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {trainDoor && (
        <TrainDoorTransition
          targetCity={trainDoor}
          onComplete={() => { const city = trainDoor; onNavigateToCity && onNavigateToCity(city); setTimeout(() => setTrainDoor(null), 50) }}
        />
      )}

      {/* ── Badge toast notification ── */}
      {badgeToast && (
        <div className="badge-toast">
          <div className="badge-toast-icon">{badgeToast.icon}</div>
          <div>
            <div className="badge-toast-title">Badge unlocked</div>
            <div className="badge-toast-name">{badgeToast.name}</div>
          </div>
        </div>
      )}
    </div>
  )
}
